import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Bell, Menu, X, User, LogOut, LayoutDashboard, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const dashboardPath = (role) =>
  ({ super_admin: "/admin", admin: "/admin", sub_admin: "/admin", educator: "/educator", content_seller: "/seller", institute: "/institute", learner: "/learner" }[role] || "/learner");

export default function Navbar() {
  const { user, logout } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (user) api.get("/notifications").then(({ data }) => setNotes(data)).catch(() => {});
  }, [user]);

  const unread = notes.filter((n) => !n.read).length;

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/courses?q=${encodeURIComponent(q)}`);
  };

  const markRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setNotes((p) => p.map((n) => ({ ...n, read: true })));
  };

  return (
    <header className="glass-nav sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img src="/viriks-logo.jpg" alt="VIRIKS" className="h-9 md:h-10 w-auto" />
          </Link>

          <form onSubmit={doSearch} className="hidden md:flex flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search courses, subjects, tests..."
              className="w-full pl-10 pr-4 py-2 rounded-full border border-border bg-white/70 focus:outline-none focus:ring-2 focus:ring-amber text-sm"
            />
          </form>

          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-navy">
            <Link to="/courses" className="hover:text-amber transition-colors" data-testid="nav-courses">Courses</Link>
            <Link to="/tests" className="hover:text-amber transition-colors" data-testid="nav-tests">Test Series</Link>
            <Link to="/store" className="hover:text-amber transition-colors" data-testid="nav-store">Store</Link>
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/cart" className="relative p-2 rounded-full hover:bg-muted transition-colors" data-testid="cart-link">
              <ShoppingCart size={20} className="text-navy" />
              {items.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-crimson text-white text-[10px] h-4 w-4 rounded-full flex items-center justify-center">{items.length}</span>
              )}
            </Link>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative p-2 rounded-full hover:bg-muted transition-colors" data-testid="notif-bell">
                    <Bell size={20} className="text-navy" />
                    {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-amber text-navy text-[10px] h-4 w-4 rounded-full flex items-center justify-center">{unread}</span>}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-2 py-1">
                    <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                    {unread > 0 && <button onClick={markRead} className="text-xs text-amber font-semibold">Mark all read</button>}
                  </div>
                  <DropdownMenuSeparator />
                  <div className="max-h-80 overflow-auto">
                    {notes.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications</p>}
                    {notes.slice(0, 12).map((n) => (
                      <div key={n.id} className={`px-3 py-2 text-sm ${!n.read ? "bg-amber/5" : ""}`}>
                        <p className="font-semibold text-navy">{n.title}</p>
                        <p className="text-muted-foreground text-xs">{n.message}</p>
                      </div>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-muted transition-colors" data-testid="user-menu">
                    <div className="h-8 w-8 rounded-full viriks-navy text-amber flex items-center justify-center font-heading font-bold text-sm">
                      {user.name?.[0]?.toUpperCase()}
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-semibold">{user.name}</div>
                    <div className="text-xs text-muted-foreground font-normal capitalize">{user.role.replace("_", " ")}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(dashboardPath(user.role))} data-testid="menu-dashboard">
                    <LayoutDashboard size={16} className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                  {user.is_institute && user.role !== "institute" && (
                    <DropdownMenuItem onClick={() => navigate("/institute")} data-testid="menu-institute-dashboard">
                      <Building2 size={16} className="mr-2" /> Institute Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/learner/profile")}>
                    <User size={16} className="mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn">
                    <LogOut size={16} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="login-nav-btn">Login</Button>
                <Button className="rounded-full viriks-navy text-white hover:bg-navy/90" onClick={() => navigate("/register")} data-testid="register-nav-btn">Get Started</Button>
              </div>
            )}

            <button className="lg:hidden p-2" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-white px-4 py-4 space-y-3" data-testid="mobile-menu">
          <form onSubmit={doSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="w-full pl-10 pr-4 py-2 rounded-full border border-border text-sm" />
          </form>
          <Link to="/courses" className="block py-2 font-medium" onClick={() => setMobileOpen(false)}>Courses</Link>
          <Link to="/tests" className="block py-2 font-medium" onClick={() => setMobileOpen(false)}>Test Series</Link>
          <Link to="/store" className="block py-2 font-medium" onClick={() => setMobileOpen(false)}>Store</Link>
          {!user && (
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => { navigate("/login"); setMobileOpen(false); }}>Login</Button>
              <Button className="flex-1 viriks-navy text-white" onClick={() => { navigate("/register"); setMobileOpen(false); }}>Sign Up</Button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
